import React, { Fragment, useContext, useEffect, useRef, useState, forwardRef } from "react"
import { fetchEmployees, reassignDoc, confirmAllEmpDocs, deleteAllEmpDocs } from "../services/api"
import { Combobox, Dialog, Transition } from "@headlessui/react"
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/24/outline"
import { MessageContext } from "../contexts/message"
import { AuthContext } from "../contexts/auth";


  
const get_employee_display_name = (emp) => {
    return [emp.name, emp.cf].filter(n => n).join(' ')
};

const requestConfirm = (message) => {
    return confirm(message);
}

export const confirmAll = async (token, docs_id, doc_type) => {
    const message = `Vuoi confermare ${docs_id.length} documenti?`;

    if(!requestConfirm(message))
        return null;

    return confirmAllEmpDocs(token, docs_id, doc_type)
};

export const deleteAll = async (token, docs_id, doc_type) => {
    const message = `Vuoi eliminare ${docs_id.length} documenti?`;

    if(!requestConfirm(message))
        return null;
        

    return deleteAllEmpDocs(token, docs_id, doc_type)
};



const ReassignForm = forwardRef(({employee}, ref) => {
    const auth = useContext(AuthContext);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(employee);
    const [query, setQuery] = useState('');
    const controller = useRef(null);

    useEffect(() => {
        if(controller.current) {
        controller.current.abort();
        }
        controller.current = new AbortController();
        const signal = controller.current.signal;
        
        // if(query === '') {
        //     setEmployees([]);
        //     return;
        // }

        fetchEmployees(auth.token, query, signal).then(response => {
        setEmployees(response.map((emp) => ({cf: emp.cf, name: [emp.last_name, emp.first_name].filter(n => n).join(' ')})));
        });

    }, [query]);

    useEffect(() => {
        if (ref) {
            if (typeof ref === 'function') {
                ref(selectedEmployee);
            } else {
                (ref).current = selectedEmployee;
            }
        }
    }, [selectedEmployee]);


    return <>
        <Combobox value={selectedEmployee} onChange={setSelectedEmployee}>
        <div className="relative mt-1">
            <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                
            <Combobox.Input onChange={(event) => setQuery(event.target.value)} 
                className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                displayValue={(emp) => get_employee_display_name(emp)} />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    />
            </Combobox.Button>
            </div>
        <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
            >
        <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {
            employees.length === 0 && query !== '' ?
            <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                Nessun Risultato.
            </div> :
            employees.map((emp) => (
                <Combobox.Option
                key={emp.cf}
                className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                    }`
                }
                value={emp}
                >
                {({ selected, active }) => (
                    <>
                    <span
                        className={`block truncate ${
                        selected ? 'font-medium' : 'font-normal'
                        }`}
                    >
                        {get_employee_display_name(emp)}
                    </span>
                    {selected ? (
                        <span
                        className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? 'text-white' : 'text-teal-600'
                        }`}
                        >
                        <CheckIcon className="h-5 w-5" />
                        </span>
                    ) : null}
                    </>
                )}
                </Combobox.Option>
            ))}
        </Combobox.Options>
        </Transition>
        </div>
        </Combobox>
    </>;
});

export function ReassignDialog({doc, getDocs}) {
    const auth = useContext(AuthContext);
    const message = useContext(MessageContext);
    const [isOpen, setIsOpen] = useState(false)
    const employee = doc?.employee || null;
    const selectedEmployeeRef = useRef(employee);


    useEffect(() => {
        setIsOpen(!!employee);
    }, [employee]);

    const handleSubmit = async () => {

        if(selectedEmployeeRef.current === null)
            return;

        try {
            await reassignDoc(auth.token, doc.id, doc.type, selectedEmployeeRef.current.cf)
            message.message.info('Assegnato con successo')
            setIsOpen(false)
            getDocs(auth.token)
        }
        catch (error) {
            console.error(error)
            message.message.error('Errore, riprova più tardi');
        }

    };
    return (
        <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setIsOpen(false)}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                >
                <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                <Dialog.Panel className="rounded-2xl bg-gray-100 p-6  w-5/12">
                    <Dialog.Title className="text-lg font-medium leading-6 text-gray-900">Riassegna il documento</Dialog.Title>
                    <div className="mt-2">
                    <ReassignForm employee={employee} ref={selectedEmployeeRef} />
                    </div>
                    <div className="mt-4">
                    <button type="button" className="inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 m-1" onClick={() => setIsOpen(false)}>Annulla</button>
                    <button type="button" className="inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 m-1" onClick={handleSubmit}>Salva</button>
                    </div>
                </Dialog.Panel>
                </Transition.Child>
            </div>
            </div>
        </Dialog>
        </Transition>
    )
}
